import { useState, useRef, useLayoutEffect } from 'react';
import { FormField } from '@/components/FormField';
import { EditableTextarea, type MarkdownMode } from '@/components/ui/editable-textarea';
import { Button } from '@/components/ui/button';
import { CharacterCount } from '@/components/ui/character-count';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Trash2 } from 'lucide-react';

export interface MarkdownEntryFormProps {
  /** Label shown above textarea */
  label: string;
  /** Placeholder when textarea is empty */
  placeholder: string;
  initialContent?: string;
  onSubmit: (content: string) => void;
  onCancel: () => void;
  onDelete?: () => void;
  isSubmitting?: boolean;
  readOnly?: boolean;
  startInEditMode?: boolean;
  maxLength?: number;
}

export function MarkdownEntryForm({
  label,
  placeholder,
  initialContent = '',
  onSubmit,
  onCancel,
  onDelete,
  isSubmitting = false,
  readOnly = false,
  startInEditMode = false,
  maxLength,
}: MarkdownEntryFormProps) {
  const [isInEditMode, setIsInEditMode] = useState(() => startInEditMode && !readOnly);
  const [content, setContent] = useState(initialContent);
  const [markdownMode, setMarkdownMode] = useState<MarkdownMode>('edit');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useLayoutEffect(() => {
    if (readOnly) {
      setIsInEditMode(false);
    } else if (startInEditMode) {
      setIsInEditMode(true);
      setMarkdownMode('edit');
    }
  }, [readOnly, startInEditMode]);

  useLayoutEffect(() => {
    if (isInEditMode && !readOnly && textareaRef.current) {
      const t = setTimeout(() => textareaRef.current?.focus(), 0);
      return () => clearTimeout(t);
    }
  }, [isInEditMode, readOnly]);

  const enterEditMode = () => {
    if (!readOnly && !isInEditMode) {
      setIsInEditMode(true);
      setMarkdownMode('edit');
    }
  };

  const handleSave = () => {
    if (maxLength !== undefined && content.length > maxLength) return;
    onSubmit(content);
    setIsInEditMode(false);
  };

  const discardChanges = () => {
    setContent(initialContent);
    setIsInEditMode(false);
    setShowCancelConfirm(false);
    onCancel();
  };

  const handleCancelClick = () => {
    if (content !== initialContent && isInEditMode) {
      setShowCancelConfirm(true);
    } else {
      discardChanges();
    }
  };

  const effectiveReadOnly = readOnly || !isInEditMode;

  return (
    <div>
      <FormField label={label}>
        <EditableTextarea
          ref={textareaRef}
          value={content}
          onChange={(e) => setContent(e.target.value)}
          markdown
          showModeToggle={isInEditMode && !readOnly}
          markdownMode={markdownMode}
          onModeChange={setMarkdownMode}
          readOnly={effectiveReadOnly}
          disabled={isSubmitting}
          placeholder={placeholder}
          maxLength={maxLength}
          rows={4}
          minHeight="min-h-[150px] md:min-h-[200px]"
          onClick={!readOnly && !isInEditMode ? enterEditMode : undefined}
        />
        {isInEditMode && !readOnly && maxLength !== undefined && (
          <CharacterCount current={content.length} max={maxLength} />
        )}
      </FormField>

      {isInEditMode && !readOnly && (
        <div className="flex justify-end gap-2 mt-3">
          {onDelete && (
            <Button
              variant="ghost"
              type="button"
              onClick={() => setShowDeleteConfirm(true)}
              disabled={isSubmitting}
              aria-label={`Delete ${label.toLowerCase()}`}
              className="mr-auto"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
          <Button variant="outline" onClick={handleCancelClick} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={
              content.trim().length === 0 ||
              (maxLength !== undefined && content.length > maxLength) ||
              isSubmitting
            }
          >
            {isSubmitting ? 'Saving...' : 'Save'}
          </Button>
        </div>
      )}

      {onDelete && (
        <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete {label.toLowerCase()}?</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete this {label.toLowerCase()}? This action cannot be
                undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={isSubmitting}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => {
                  onDelete();
                  setShowDeleteConfirm(false);
                }}
                disabled={isSubmitting}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}

      {showCancelConfirm && (
        <AlertDialog open={showCancelConfirm} onOpenChange={setShowCancelConfirm}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Discard changes?</AlertDialogTitle>
              <AlertDialogDescription>
                You have unsaved changes. Discard them?
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={isSubmitting}>Continue Editing</AlertDialogCancel>
              <AlertDialogAction
                onClick={discardChanges}
                disabled={isSubmitting}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                Discard
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </div>
  );
}
